import { createAsyncThunk } from '@reduxjs/toolkit';
import { doc, getDoc, updateDoc, serverTimestamp, increment, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import { setSaveStatus, setShareStatus, addToSavedSolutions, removeFromSavedSolutions } from '../solutionSlice.js';
import { normalizeSolutionData } from './utils.js';

/**
 * Toggle save for a solution
 */
export const toggleSaveSolution = createAsyncThunk(
  'solutions/toggleSave',
  async ({ solutionId, userId, save }, { rejectWithValue, dispatch }) => {
    // For optimistic add/revert in Files > Solutions list
    let normalized = null;
    try {
      if (!solutionId || !userId) {
        throw new Error('Solution ID and User ID are required');
      }

      const solutionRef = doc(db, 'solutions', solutionId);
      const solutionDoc = await getDoc(solutionRef);

      if (!solutionDoc.exists()) {
        throw new Error('Solution not found');
      }

      // Normalize once for optimistic list updates
      normalized = normalizeSolutionData(solutionDoc);

      const data = solutionDoc.data() || {};
      const savers = Array.isArray(data.savers) ? data.savers : [];
      const hadSaved = savers.includes(userId);

      // Optimistic update for current solution and list-level
      const postId = normalized?.postId || (solutionDoc.data() || {}).postId;
      dispatch(setSaveStatus({ saved: save, userId, solutionId, postId }));
      // Optimistic update for saved list in Files tab
      if (save) {
        // Add only if not present
        if (normalized) dispatch(addToSavedSolutions(normalized));
      } else {
        // Remove immediately on unsave for instant UX
        dispatch(removeFromSavedSolutions({ solutionId }));
      }

      // Only apply count change if membership is actually changing
      const updates = { updatedAt: serverTimestamp() };
      if (save && !hadSaved) {
        updates.saves = increment(1);
        updates.savers = arrayUnion(userId);
      } else if (!save && hadSaved) {
        updates.saves = increment(-1);
        updates.savers = arrayRemove(userId);
      } else {
        // No-op for saves count; ensure savers array is correct
        updates.savers = save ? arrayUnion(userId) : arrayRemove(userId);
      }

      await updateDoc(solutionRef, updates);

      return { saved: save, userId };
    } catch (error) {
      // Revert optimistic update
      dispatch(setSaveStatus({ saved: !save, userId, solutionId, postId: normalized?.postId || undefined }));
      // Revert saved list change
      if (save) {
        // We attempted to save; remove if we added
        dispatch(removeFromSavedSolutions({ solutionId }));
      } else {
        // We attempted to unsave; re-add original if available
        if (normalized) dispatch(addToSavedSolutions(normalized));
      }
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Toggle share for a solution
 */
export const toggleShareSolution = createAsyncThunk(
  'solutions/toggleShare',
  async ({ solutionId, userId, share }, { rejectWithValue, dispatch }) => {
    try {
      if (!solutionId || !userId) {
        throw new Error('Solution ID and User ID are required');
      }

      const solutionRef = doc(db, 'solutions', solutionId);
      const solutionDoc = await getDoc(solutionRef);

      if (!solutionDoc.exists()) {
        throw new Error('Solution not found');
      }

      const data = solutionDoc.data() || {};
      const postId = data.postId;

      // Optimistic update for current solution and list-level
      dispatch(setShareStatus({ shared: share, userId, solutionId, postId }));

      // Use "Total Share Events" logic (Facebook/Industry standard)
      // Every share action increments the count
      const updates = {
        updatedAt: serverTimestamp(),
        shares: increment(1),
        sharers: arrayUnion(userId) // Maintain list of unique sharers
      };

      await updateDoc(solutionRef, updates);

      return { shared: true, userId, solutionId };
    } catch (error) {
      // Revert optimistic update
      dispatch(setShareStatus({ shared: !share, userId, solutionId }));
      return rejectWithValue(error.message);
    }
  }
);
